/**
 * useKeyboardShortcut — Web-only keyboard shortcut hook.
 * No-op on native (iOS/Android).
 * Skips when an input/textarea element is focused.
 */

import { useEffect } from 'react';
import { Platform } from 'react-native';

interface ShortcutOptions {
  ctrl?: boolean;
  shift?: boolean;
  meta?: boolean;
  enabled?: boolean;
}

/**
 * Register a keyboard shortcut that fires only on web.
 */
export function useKeyboardShortcut(
  key: string,
  handler: () => void,
  options: ShortcutOptions = {}
): void {
  const { ctrl = false, shift = false, meta = false, enabled = true } = options;

  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (ctrl && !e.ctrlKey) return;
      if (shift && !e.shiftKey) return;
      if (meta && !e.metaKey) return;
      if (!ctrl && e.ctrlKey) return;
      if (!shift && e.shiftKey) return;
      if (!meta && e.metaKey) return;

      if (e.key === key) {
        e.preventDefault();
        handler();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [key, handler, ctrl, shift, meta, enabled]);
}

/**
 * Register number-key shortcuts (1-9) for option selection.
 * No-op on native.
 */
export function useNumberKeyShortcuts(
  count: number,
  onSelect: (index: number) => void,
  enabled = true
): void {
  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= count) {
        e.preventDefault();
        onSelect(num - 1);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [count, onSelect, enabled]);
}
